import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Camera, DollarSign, Users as UsersIcon, Shield, AlertCircle, Trash2 } from 'lucide-react';
import { API_URL } from '../../config';

export default function UserDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('avatars');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showCreditsModal, setShowCreditsModal] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [processingCredits, setProcessingCredits] = useState(false);
  
  // NEU: Bilder hinzufügen/abziehen State
  const [showImagesModal, setShowImagesModal] = useState(null);  // 'add' or 'subtract'
  const [imagesAmount, setImagesAmount] = useState('');
  const [imagesReason, setImagesReason] = useState('');
  const [processingImages, setProcessingImages] = useState(false);
  
  // Audit-Log Pagination
  const [auditLimit, setAuditLimit] = useState(20);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch user details');
      
      const userData = await res.json();
      setData(userData);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteUser = async () => {
    if (!data || !data.user) return;
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/user/${data.user.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: data.user.id })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Löschen fehlgeschlagen');
      }
      
      const result = await res.json();
      console.log('User deleted successfully:', result);
      
      // Show success message and return to users list
      alert(`User ${data.user.email} wurde erfolgreich gelöscht.\n\nCascade:\n- Consents: ${result.cascade.consents}\n- Avatare: ${result.cascade.avatars}\n- Media: ${result.cascade.media}\n- Transaktionen: ${result.cascade.transactions}`);
      
      // Go back to users list
      onBack();
      
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Fehler beim Löschen: ${error.message}`);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle Credits Add/Subtract
  const handleCreditsAction = async () => {
    if (!creditsAmount || parseInt(creditsAmount) < 1) {
      alert('Bitte mindestens 1 SNAP eingeben');
      return;
    }
    
    if (!creditsReason.trim()) {
      alert('Bitte Grund angeben');
      return;
    }
    
    setProcessingCredits(true);
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const action = showCreditsModal; // 'add' or 'subtract'
      const amount = parseInt(creditsAmount);
      
      const res = await fetch(`${API_URL}/api/admin/credits/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: data.user.id,
          amount: amount,
          reason: creditsReason
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Credits operation failed');
      }
      
      const result = await res.json();
      console.log('Credits operation successful:', result);
      
      // Show success
      alert(`Credits ${action === 'add' ? 'hinzugefügt' : 'abgezogen'}: ${amount} SNAPs\nNeues Guthaben: ${result.new_balance} SNAPs`);
      
      // Refresh user details immediately
      await fetchUserDetails();
      
      // Close modal & reset
      setShowCreditsModal(null);
      setCreditsAmount('');
      setCreditsReason('');
      
    } catch (error) {
      console.error('Error with credits operation:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setProcessingCredits(false);
    }
  };
  
  // NEU: Handle Images Add/Subtract
  const handleImagesAction = async () => {
    if (!imagesAmount || parseInt(imagesAmount) < 1) {
      alert('Bitte mindestens 1 Bild eingeben');
      return;
    }
    
    if (!imagesReason.trim()) {
      alert('Bitte Grund angeben');
      return;
    }
    
    setProcessingImages(true);
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const action = showImagesModal; // 'add' or 'subtract'
      const endpoint = action === 'add'
        ? `${API_URL}/api/admin/users/${userId}/add-images`
        : `${API_URL}/api/admin/users/${userId}/subtract-images`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseInt(imagesAmount),
          reason: imagesReason.trim()
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Fehler');
      }
      
      const result = await res.json();
      alert(`✅ ${imagesAmount} Bilder erfolgreich ${action === 'add' ? 'hinzugefügt' : 'abgezogen'}!`);
      
      // Reset & refresh
      setImagesAmount('');
      setImagesReason('');
      setShowImagesModal(null);
      await fetchUserDetails();
      
    } catch (error) {
      console.error('Error with images operation:', error);
      alert(`Fehler: ${error.message}`);
    } finally {
      setProcessingImages(false);
    }
  };


  const getStatusBadge = (status) => {
    const colors = {
      'ready': 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/50',
      'training': 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
      'error': 'bg-red-500/20 text-red-500 border-red-500/50',
      'pending': 'bg-white/20 text-white border-white/50',
      'paid': 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/50',
      'active': 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/50',
      'blocked': 'bg-red-500/20 text-red-500 border-red-500/50'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${colors[status] || colors.pending}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-white text-lg">User nicht gefunden</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all"
          >
            Zurück
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'avatars', label: 'Avatare', icon: Camera, count: data.avatars.length },
    { id: 'transactions', label: 'Transaktionen', icon: DollarSign, count: data.transactions.length },
    { id: 'referrals', label: 'Referrals', icon: UsersIcon, count: data.referrals.successful_registrations },
    { id: 'audit', label: 'Audit-Log', icon: Shield, count: data.audit.length }
  ];

  return (
    <div className="min-h-screen bg-black grain-texture text-white p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            data-testid="user-detail-back-button"
            className="flex items-center gap-2 text-white/60 hover:text-[#10B981] transition-all mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Zurück zur Nutzerliste
          </button>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <User className="w-8 h-8 text-[#10B981]" />
                <h1 className="text-4xl font-bold">
                  User #{data.user.display_id ? String(data.user.display_id).padStart(6, '0') : '------'}
                </h1>
              </div>
              <div className="flex items-center gap-4 text-white/60">
                <span>{data.user.email}</span>
                <span>•</span>
                <span className="text-xs">UUID: {data.user.id.substring(0, 12)}...</span>
                <span>•</span>
                {getStatusBadge(data.user.status)}
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="text-right">
                <div className="text-3xl font-bold text-[#10B981]">{Number((data.user.credits || data.user.snaps || 0).toFixed(2))} SNAPs</div>
                <div className="text-xs text-white/50">Erstellt: {formatDate(data.user.created_at)}</div>
              </div>
              
              {/* Admin Action Buttons - Icon Row */}
              <div className="flex items-center gap-2">
                {/* Lock/Unlock */}
                <button
                  title="Account sperren"
                  className="p-2 rounded-lg border-2 border-white/20 text-white/60 hover:border-yellow-500 hover:text-yellow-500 transition-all"
                >
                  <Shield className="w-5 h-5" />
                </button>
                
                {/* Credits Add */}
                <button
                  onClick={() => setShowCreditsModal('add')}
                  data-testid="add-credits-button"
                  title="Credits geben"
                  className="p-2 rounded-lg border-2 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20 hover:border-[#10B981] transition-all"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                
                {/* Credits Subtract */}
                <button
                  onClick={() => setShowCreditsModal('subtract')}
                  data-testid="subtract-credits-button"
                  title="Credits abziehen"
                  className="p-2 rounded-lg border-2 border-orange-500/30 text-orange-500 hover:bg-orange-500/20 hover:border-orange-500 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                  </svg>
                </button>
                
                {/* NEU: Bilder hinzufügen */}
                <button
                  onClick={() => setShowImagesModal('add')}
                  data-testid="add-images-button"
                  title="Kostenlose Bilder geben"
                  className="p-2 rounded-lg border-2 border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/20 hover:border-emerald-400 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </button>
                
                {/* NEU: Bilder abziehen */}
                <button
                  onClick={() => setShowImagesModal('subtract')}
                  data-testid="subtract-images-button"
                  title="Kostenlose Bilder abziehen"
                  className="p-2 rounded-lg border-2 border-red-400/30 text-red-400 hover:bg-red-400/20 hover:border-red-400 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Delete User (Danger) */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  data-testid="delete-user-button"
                  title="User löschen"
                  className="p-2 rounded-lg border-2 border-red-500/30 text-red-500 hover:bg-red-500/20 hover:border-red-500 transition-all"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-4">
            {/* User Info */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <User className="w-6 h-6 text-[#10B981]" />
                <h1 className="text-2xl font-bold">
                  User #{data.user.display_id ? String(data.user.display_id).padStart(6, '0') : '------'}
                </h1>
              </div>
              <div className="text-white/60 text-sm space-y-1">
                <div>{data.user.email}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs">UUID: {data.user.id.substring(0, 12)}...</span>
                  {getStatusBadge(data.user.status)}
                </div>
              </div>
            </div>

            {/* SNAPs Display */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="text-4xl font-bold text-[#10B981] mb-1">
                {Number((data.user.credits || data.user.snaps || 0).toFixed(2))} SNAPs
              </div>
              <div className="text-xs text-white/50">
                Erstellt: {formatDate(data.user.created_at)}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowCreditsModal('add')}
                data-testid="add-credits-button-mobile"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-[#10B981]/30 text-[#10B981] hover:bg-[#10B981]/20 hover:border-[#10B981] transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                <span className="font-medium">Hinzufügen</span>
              </button>

              <button
                onClick={() => setShowCreditsModal('subtract')}
                data-testid="subtract-credits-button-mobile"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-orange-500/30 text-orange-500 hover:bg-orange-500/20 hover:border-orange-500 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
                </svg>
                <span className="font-medium">Abziehen</span>
              </button>

              <button
                onClick={() => setShowImagesModal('add')}
                data-testid="add-images-button-mobile"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-emerald-400/30 text-emerald-400 hover:bg-emerald-400/20 hover:border-emerald-400 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                <span className="font-medium">Bilder geben</span>
              </button>

              <button
                onClick={() => setShowImagesModal('subtract')}
                data-testid="subtract-images-button-mobile"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-red-400/30 text-red-400 hover:bg-red-400/20 hover:border-red-400 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                <span className="font-medium">Bilder abziehen</span>
              </button>

              <button
                title="Account sperren"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-white/20 text-white/60 hover:border-yellow-500 hover:text-yellow-500 transition-all"
              >
                <Shield className="w-5 h-5" />
                <span className="font-medium">Sperren</span>
              </button>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-user-button-mobile"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-red-500/30 text-red-500 hover:bg-red-500/20 hover:border-red-500 transition-all"
              >
                <Trash2 className="w-5 h-5" />
                <span className="font-medium">Löschen</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6 border-b-2 border-white/10 -mx-4 md:mx-0 px-4 md:px-0">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide"
               style={{
                 scrollbarWidth: 'none',
                 msOverflowStyle: 'none',
                 WebkitOverflowScrolling: 'touch'
               }}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 md:px-6 py-3 border-b-2 transition-all whitespace-nowrap flex-shrink-0
                    ${activeTab === tab.id 
                      ? 'border-[#10B981] text-[#10B981] font-bold' 
                      : 'border-transparent text-white/60 hover:text-white hover:border-white/30'}
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                  <span className={`
                    px-2 py-0.5 rounded-full text-xs font-medium
                    ${activeTab === tab.id ? 'bg-[#10B981]/20' : 'bg-white/10'}
                  `}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-black border-2 border-white/10 rounded-xl p-6 grain-texture">
          
          {/* Tab 1: Avatare */}
          {activeTab === 'avatars' && (
            <div data-testid="tab-content-avatars">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Camera className="w-6 h-6 text-[#10B981]" />
                Avatare ({data.avatars.length})
              </h2>
              
              {data.avatars.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  <Camera className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Keine Avatare vorhanden</p>
                </div>
              ) : (
                <>
                  {/* Desktop: Tabelle */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-white/10">
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Name</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Status</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Tune ID</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Erstellt</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Läuft ab</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Generierungen</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Kosten</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Marge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.avatars.map((avatar, idx) => (
                          <tr 
                            key={avatar.id} 
                            className="border-b border-white/5 hover:bg-white/5 transition-colors"
                            data-testid={`avatar-row-${idx}`}
                          >
                            <td className="py-4 px-4 text-sm font-medium">{avatar.title || 'Unbenannt'}</td>
                            <td className="py-4 px-4">{getStatusBadge(avatar.status)}</td>
                            <td className="py-4 px-4 text-sm font-mono">{avatar.tune_id != null ? String(avatar.tune_id).substring(0, 12) : 'N/A'}{avatar.tune_id && String(avatar.tune_id).length > 12 ? '...' : ''}</td>
                            <td className="py-4 px-4 text-sm">{formatDate(avatar.created_at)}</td>
                            <td className="py-4 px-4 text-sm">{formatDate(avatar.expires_at)}</td>
                            <td className="py-4 px-4 text-right text-sm">{avatar.generations_count}</td>
                            <td className="py-4 px-4 text-right text-sm">€{avatar.total_cost}</td>
                            <td className={`py-4 px-4 text-right text-sm font-bold ${avatar.margin >= 0 ? 'text-[#10B981]' : 'text-red-500'}`}>
                              €{avatar.margin}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Karten */}
                  <div className="md:hidden space-y-4">
                    {data.avatars.map((avatar, idx) => (
                      <div 
                        key={avatar.id}
                        className="bg-white/5 border border-white/10 rounded-lg p-4"
                        data-testid={`avatar-card-${idx}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {getStatusBadge(avatar.status)}
                            <span className="font-medium text-white">{avatar.title || 'Unbenannt'}</span>
                          </div>
                          <div className="text-right">
                            <div className={`text-lg font-bold ${avatar.margin >= 0 ? 'text-[#10B981]' : 'text-red-500'}`}>
                              €{avatar.margin}
                            </div>
                            <div className="text-xs text-white/50">Marge</div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/60">Tune ID:</span>
                            <span className="font-mono">{avatar.tune_id != null ? String(avatar.tune_id).substring(0, 12) : 'N/A'}{avatar.tune_id && String(avatar.tune_id).length > 12 ? '...' : ''}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Erstellt:</span>
                            <span>{formatDate(avatar.created_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Läuft ab:</span>
                            <span>{formatDate(avatar.expires_at)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Generierungen:</span>
                            <span className="font-medium">{avatar.generations_count}</span>
                          </div>
                          <div className="flex justify-between border-t border-white/10 pt-2">
                            <span className="text-white/60">Kosten:</span>
                            <span className="font-bold">€{avatar.total_cost}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab 2: Transaktionen */}
          {activeTab === 'transactions' && (
            <div data-testid="tab-content-transactions">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-[#10B981]" />
                Transaktionen ({data.transactions.length})
              </h2>
              
              {data.transactions.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Keine Transaktionen vorhanden</p>
                </div>
              ) : (
                <>
                  {/* Desktop: Tabelle */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-white/10">
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Datum</th>
                          <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Paket</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Brutto</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Netto</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">MwSt.</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Stripe</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Astria</th>
                          <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Marge</th>
                          <th className="text-center py-3 px-4 text-white/60 font-medium text-sm">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.transactions.map((tx, idx) => (
                          <tr 
                            key={tx.id} 
                            className="border-b border-white/5 hover:bg-white/5 transition-colors"
                            data-testid={`transaction-row-${idx}`}
                          >
                            <td className="py-4 px-4 text-sm">{formatDate(tx.created_at)}</td>
                            <td className="py-4 px-4 text-sm font-medium">{tx.package_name}</td>
                            <td className="py-4 px-4 text-right text-sm font-bold">€{tx.brutto}</td>
                            <td className="py-4 px-4 text-right text-sm">€{tx.netto}</td>
                            <td className="py-4 px-4 text-right text-sm text-white/60">€{tx.mwst}</td>
                            <td className="py-4 px-4 text-right text-sm text-red-400">€{tx.stripe_fee}</td>
                            <td className="py-4 px-4 text-right text-sm text-orange-400">€{tx.astria_total}</td>
                            <td className={`py-4 px-4 text-right text-sm font-bold ${tx.margin >= 0 ? 'text-[#10B981]' : 'text-red-500'}`}>
                              €{tx.margin}
                            </td>
                            <td className="py-4 px-4 text-center">{getStatusBadge(tx.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: Karten */}
                  <div className="md:hidden space-y-4">
                    {data.transactions.map((tx, idx) => (
                      <div 
                        key={tx.id}
                        className="bg-white/5 border border-white/10 rounded-lg p-4"
                        data-testid={`transaction-card-${idx}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="font-bold text-lg">{tx.package_name}</div>
                          {getStatusBadge(tx.status)}
                        </div>
                        
                        <div className="space-y-2 text-sm mb-3">
                          <div className="flex justify-between text-white/60 text-xs">
                            <span>Datum:</span>
                            <span>{formatDate(tx.created_at)}</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm border-t border-white/10 pt-3">
                          <div>
                            <div className="text-white/60 text-xs mb-1">Brutto</div>
                            <div className="font-bold text-lg">€{tx.brutto}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-white/60 text-xs mb-1">Marge</div>
                            <div className={`font-bold text-lg ${tx.margin >= 0 ? 'text-[#10B981]' : 'text-red-500'}`}>
                              €{tx.margin}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm mt-3 border-t border-white/10 pt-3">
                          <div className="flex justify-between">
                            <span className="text-white/60">Netto:</span>
                            <span>€{tx.netto}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">MwSt.:</span>
                            <span>€{tx.mwst}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Stripe Gebühr:</span>
                            <span className="text-red-400">€{tx.stripe_fee}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/60">Astria Kosten:</span>
                            <span className="text-orange-400">€{tx.astria_total}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Tab 3: Referrals */}
          {activeTab === 'referrals' && (
            <div data-testid="tab-content-referrals">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <UsersIcon className="w-6 h-6 text-[#10B981]" />
                Referral-Statistiken
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="p-6 bg-white/5 border-2 border-white/10 rounded-xl">
                  <div className="text-sm text-white/60 mb-2">Total Invites</div>
                  <div className="text-3xl font-bold text-white">{data.referrals.total_invites}</div>
                </div>
                <div className="p-6 bg-white/5 border-2 border-white/10 rounded-xl">
                  <div className="text-sm text-white/60 mb-2">Registrierungen</div>
                  <div className="text-3xl font-bold text-[#10B981]">{data.referrals.successful_registrations}</div>
                </div>
                <div className="p-6 bg-white/5 border-2 border-white/10 rounded-xl">
                  <div className="text-sm text-white/60 mb-2">Käufe</div>
                  <div className="text-3xl font-bold text-[#10B981]">{data.referrals.successful_purchases}</div>
                </div>
                <div className="p-6 bg-white/5 border-2 border-white/10 rounded-xl">
                  <div className="text-sm text-white/60 mb-2">Verdiente SNAPs</div>
                  <div className="text-3xl font-bold text-[#10B981]">{data.referrals.earned_snaps}</div>
                </div>
              </div>

              {data.referrals.current_referral_code && (
                <div className="p-6 bg-[#10B981]/10 border-2 border-[#10B981]/30 rounded-xl mb-6">
                  <div className="text-sm text-white/60 mb-2">Aktueller Referral-Code</div>
                  <div className="text-xl font-mono font-bold text-[#10B981]">{data.referrals.current_referral_code}</div>
                </div>
              )}

              {data.referrals.was_referred_by && (
                <div className="p-6 bg-white/5 border-2 border-white/10 rounded-xl">
                  <div className="text-sm text-white/60 mb-2">Wurde eingeladen von</div>
                  <div className="text-lg font-medium">{data.referrals.was_referred_by.referrer_email}</div>
                  <div className="text-xs text-white/40 mt-1">ID: {data.referrals.was_referred_by.referrer_id}</div>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Audit-Log */}
          {activeTab === 'audit' && (
            <div data-testid="tab-content-audit">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6 text-[#10B981]" />
                Audit-Log ({data.audit.length})
              </h2>
              
              {data.audit.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  <Shield className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>Keine Aktivitäten vorhanden</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.audit.slice(0, auditLimit).map((log, idx) => (
                    <div 
                      key={log.id} 
                      className={`p-4 border-2 rounded-xl hover:border-white/20 transition-all ${
                        log.source === 'admin' 
                          ? 'bg-purple-500/10 border-purple-500/30' 
                          : 'bg-white/5 border-white/10'
                      }`}
                      data-testid={`audit-row-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              log.source === 'admin' 
                                ? 'bg-purple-500/30 text-purple-300' 
                                : 'bg-blue-500/30 text-blue-300'
                            }`}>
                              {log.source === 'admin' ? '👑 Admin' : '👤 User'}
                            </span>
                            <span className="text-sm font-medium text-[#10B981]">{log.action_type}</span>
                          </div>
                          <div className="text-xs text-white/40 mt-1">{formatDate(log.timestamp)}</div>
                        </div>
                        {log.source === 'admin' && log.admin_email && (
                          <div className="text-xs text-white/60">
                            von: <span className="font-medium">{log.admin_email}</span>
                          </div>
                        )}
                        {log.source === 'user' && log.item_type && (
                          <div className="text-xs text-white/60">
                            {log.item_type}
                          </div>
                        )}
                      </div>
                      
                      {(log.old_value || log.new_value) && (
                        <div className="flex items-center gap-3 mb-2 text-sm">
                          <span className="text-red-400">{log.old_value}</span>
                          <span className="text-white/40">→</span>
                          <span className="text-[#10B981]">{log.new_value}</span>
                        </div>
                      )}
                      
                      {log.reason && (
                        <div className="text-sm text-white/60 border-t border-white/5 pt-2 mt-2">
                          <span className="font-medium">Grund:</span> {log.reason}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Mehr anzeigen Button */}
                  {data.audit.length > auditLimit && (
                    <button
                      onClick={() => setAuditLimit(prev => prev + 50)}
                      className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white/70 hover:text-white transition-all"
                    >
                      Mehr anzeigen ({data.audit.length - auditLimit} weitere)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

        </div>

      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-red-500 rounded-xl p-8 max-w-md w-full grain-texture">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">User löschen?</h2>
              <p className="text-white/60 mb-4">
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              
              {data && data.user && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4 text-left">
                  <p className="text-white/80 text-sm mb-2">
                    <span className="font-medium">User:</span> {data.user.email}
                  </p>
                  <p className="text-white/80 text-sm mb-2">
                    <span className="font-medium">Name:</span> {data.user.name || 'N/A'}
                  </p>
                  <p className="text-white/60 text-xs mb-3">
                    UUID: {data.user.id}
                  </p>
                  <p className="text-white/60 text-xs mb-2">
                    Folgende Daten werden gelöscht:
                  </p>
                  <ul className="text-white/60 text-xs space-y-1 list-disc list-inside">
                    <li>Account-Daten & Login</li>
                    <li>{data.avatars.length} Avatare</li>
                    <li>{data.transactions.length} Transaktionen</li>
                    <li>Alle Generationen & Media</li>
                    <li>Consent-Logs</li>
                  </ul>
                </div>
              )}
              
              {/* Double-Confirm: Text-Eingabe */}
              <div className="mb-4">
                <label className="text-white/80 text-sm mb-2 block">
                  Bitte "LÖSCHEN" eingeben zur Bestätigung:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                  placeholder="LÖSCHEN"
                  className="w-full px-4 py-3 bg-black border-2 border-red-500/30 rounded-lg text-white text-center font-bold focus:border-red-500 outline-none"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleting}
                data-testid="cancel-delete-button"
                className="flex-1 px-4 py-3 bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleting || deleteConfirmText !== 'LÖSCHEN'}
                data-testid="confirm-delete-button"
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Löschen...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    User löschen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credits Modal (Add/Subtract) */}
      {showCreditsModal && data && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-[#10B981] rounded-xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-4">
              {showCreditsModal === 'add' ? '➕ Credits geben' : '➖ Credits abziehen'}
            </h2>
            
            <div className="mb-4">
              <p className="text-white/70 text-sm mb-2">User: {data.user.email}</p>
              <p className="text-white/50 text-xs">Aktuell: {Math.round((data.user.credits || data.user.snaps || 0) * 100) / 100} SNAPs</p>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Amount Input */}
              <div>
                <label className="text-white/80 text-sm mb-2 block">Anzahl SNAPs</label>
                <input
                  type="number"
                  min="1"
                  value={creditsAmount}
                  onChange={(e) => setCreditsAmount(e.target.value)}
                  placeholder="z.B. 10"
                  className="w-full px-4 py-3 bg-black border-2 border-white/20 rounded-lg text-white focus:border-[#10B981] outline-none"
                />
              </div>
              
              {/* Reason Input */}
              <div>
                <label className="text-white/80 text-sm mb-2 block">Grund (Pflicht)</label>
                <input
                  type="text"
                  value={creditsReason}
                  onChange={(e) => setCreditsReason(e.target.value)}
                  placeholder="z.B. Kulanz, Test Live"
                  className="w-full px-4 py-3 bg-black border-2 border-white/20 rounded-lg text-white focus:border-[#10B981] outline-none"
                />
              </div>
              
              {/* Preview */}
              {creditsAmount && parseInt(creditsAmount) > 0 && (
                <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                  <p className="text-white/60 text-xs mb-1">Vorschau:</p>
                  <p className="text-white font-bold">
                    Neues Guthaben: {
                      Math.round((
                        showCreditsModal === 'add' 
                          ? (data.user.credits || data.user.snaps || 0) + parseInt(creditsAmount)
                          : Math.max(0, (data.user.credits || data.user.snaps || 0) - parseInt(creditsAmount))
                      ) * 100) / 100
                    } SNAPs
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowCreditsModal(null);
                  setCreditsAmount('');
                  setCreditsReason('');
                }}
                disabled={processingCredits}
                className="flex-1 px-4 py-3 bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreditsAction}
                disabled={processingCredits || !creditsAmount || !creditsReason.trim()}
                className={`flex-1 px-4 py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  showCreditsModal === 'add'
                    ? 'bg-[#10B981] text-black hover:bg-[#059669]'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                {processingCredits ? 'Wird verarbeitet...' : showCreditsModal === 'add' ? 'Credits geben' : 'Credits abziehen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEU: Images Modal (Add) */}
      {showImagesModal && data && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-emerald-400 rounded-xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-emerald-400 mb-4">
              {showImagesModal === 'add' ? '🎁 Kostenlose Bilder geben' : '🔻 Kostenlose Bilder abziehen'}
            </h2>
            
            <div className="mb-4">
              <p className="text-white/70 text-sm mb-2">User: {data.user.email}</p>
              <p className="text-white/50 text-xs">Aktuell: {data.user.free_images || 0} Bilder (verdient: {data.user.free_images_earned || 0})</p>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Amount Input */}
              <div>
                <label className="text-white/80 text-sm mb-2 block">Anzahl Bilder</label>
                <input
                  type="number"
                  min="1"
                  value={imagesAmount}
                  onChange={(e) => setImagesAmount(e.target.value)}
                  placeholder="z.B. 10"
                  className="w-full px-4 py-3 bg-black border-2 border-emerald-400/30 rounded-lg text-white focus:border-emerald-400 outline-none"
                />
              </div>
              
              {/* Reason Input */}
              <div>
                <label className="text-white/80 text-sm mb-2 block">Grund (Pflicht)</label>
                <input
                  type="text"
                  value={imagesReason}
                  onChange={(e) => setImagesReason(e.target.value)}
                  placeholder="z.B. Kulanz, Test, Bonus"
                  className="w-full px-4 py-3 bg-black border-2 border-emerald-400/30 rounded-lg text-white focus:border-emerald-400 outline-none"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowImagesModal(null);
                  setImagesAmount('');
                  setImagesReason('');
                }}
                disabled={processingImages}
                className="flex-1 px-4 py-3 bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-all disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleImagesAction}
                disabled={processingImages || !imagesAmount || !imagesReason.trim()}
                className={`flex-1 px-4 py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  showImagesModal === 'add'
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {processingImages ? 'Wird verarbeitet...' : (showImagesModal === 'add' ? 'Bilder geben' : 'Bilder abziehen')}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}
